/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {AbstractService} from '@amblique/base/shared/services/abstract-service';
import {PREVIEW_HEADER_NAME, PREVIEW_PROXY_PATH} from '@amblique/contentful/shared/constants/preview';
import {getPreviewSecret} from '@amblique/contentful/app/utils/preview-secret';

/**
 * ContentfulService - Contentful CMS Integration
 *
 * A streamlined service for working with Contentful content using pre-generated queries.
 *
 * Key Features:
 * - Type-safe: Leverages global Contentful types for IDE autocomplete
 * - Uses pre-generated GraphQL queries from `shared/graphql/queries/`
 * - Automatic locale from current site
 * - Preview mode support
 * - Comprehensive error handling
 * - Query result transformation utilities
 *
 * Type System:
 * - Uses global `Contentful` namespace from global.d.ts
 * - Specify concrete types (e.g., Contentful.Page, Contentful.Blog) based on your queries
 * - All Contentful types are auto-generated from your schema
 *
 * Usage:
 * ```javascript
 * import {print} from 'graphql';
 * import {getPageBySlug} from '@amblique/contentful/shared/graphql/queries';
 * import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
 *
 * const contentful = new ContentfulService({site});
 * const data = await contentful.query(print(getPageBySlug), {
 *   slug: 'home',
 *   locale: contentful.locale, // Automatically set from site
 *   preview: false
 * });
 * const page = data.pageCollection?.items?.[0] || null;
 * ```
 *
 * @extends AbstractService
 */
export class ContentfulService extends AbstractService {
  /**
   * Opts this service into `useService`'s preview-flag plumbing. When a preview
   * secret has been captured (in memory, from the `?secret=` URL), `useService`
   * folds `preview` into the instance variables, which (a) isolates the React
   * Query cache for drafts vs published and (b) drives the preview-mode branch in
   * this class' constructor + `query()`.
   *
   * @see docs/contentful/preview/README.md §5.3
   * @type {boolean}
   */
  static previewAware = true;

  /**
   * @param {string} key
   * @param {object} variables
   */
  constructor(key, variables = {}) {
    super(key, variables);

    // Content preview mode: instead of calling graphql.contentful.com directly
    // with the *delivery* token (which would leak the preview token if we swapped
    // it in client-side), point at the same-origin server proxy. The proxy injects
    // the Preview API token server-side, so the token never reaches the browser.
    // See server/middleware/contentful-graphql.js and README §5.2.
    if (variables?.preview) {
      this.preview = true;

      const origin = String(variables.origin || '').replace(/\/+$/, '');

      this.baseUrl = `${origin}${PREVIEW_PROXY_PATH}`.replace(/\/+$/, '');

      // The proxy supplies Authorization; never send the delivery token to it.
      if (this.options?.headers?.Authorization) {
        delete this.options.headers.Authorization;
      }
    }
  }

  /**
   * SFCC uses en-MY for Malaysia; Contentful has no en-MY locale (KINDW-1166).
   * Use "en" for GraphQL locale when site is kl-my and the shopper locale is en-MY.
   *
   * @param {object} variables - useService variables (locale, site, …)
   * @returns {object}
   */
  static mapVariablesForContentfulLocale(variables) {
    const {locale, site} = variables || {};

    if (!locale?.id || site?.id !== 'kl-my') {
      return variables;
    }

    const normalized = String(locale.id).replace(/_/g, '-').toLowerCase();

    if (normalized !== 'en-my') {
      return variables;
    }

    return {
      ...variables,
      locale: {...locale, id: 'en'},
    };
  }

  /**
   * @param {object} variables
   * @returns {ContentfulService}
   */
  static instantiate(variables) {
    return super.instantiate(ContentfulService.mapVariablesForContentfulLocale(variables));
  }

  /**
   * @returns {string} Service identifier
   */
  static get name() {
    return 'ContentfulService';
  }

  /**
   * Service configuration profile
   * Uses Contentful GraphQL Content Delivery API (or Preview API)
   *
   * @returns {Object} Configuration object with baseUrl, options, and dynamic properties
   */
  static get profile() {
    return {
      baseUrl: 'https://graphql.contentful.com/content/v1/spaces/{site.preferences.contentful.spaceId}/environments/{site.preferences.contentful.environment}',
      locale: '{locale.id}',
      dynamicProperties: ['baseUrl', 'locale', 'options.headers.Authorization'],
      options: {
        headers: {
          'Authorization': 'Bearer {site.preferences.contentful.deliveryAccessToken}',
          'Content-Type': 'application/json',
        },
      },
    };
  }

  /**
   * Required template variables for service instantiation
   *
   * @returns {string[]} Array of required variable names
   */
  static get variables() {
    // `origin` is needed to build an absolute URL for the same-origin preview
    // proxy. It is excluded from the instance hash by `AbstractService.instantiate`
    // (origin/token are stripped), so it does not fragment the cache.
    return ['locale', 'origin', 'site'];
  }

  /**
   * Execute a GraphQL query
   *
   * Use pre-generated queries from `@amblique/contentful/shared/graphql/queries` for type safety.
   *
   * @example
   * import {print} from 'graphql';
   * import {getPageBySlug, getAllPages} from '@amblique/contentful/shared/graphql/queries';
   *
   * // Fetch by slug
   * const data = await contentful.query(print(getPageBySlug), {
   *   slug: 'home',
   *   locale: contentful.locale, // Automatically from site
   *   preview: false
   * });
   * const page = data.pageCollection?.items?.[0] || null;
   *
   * // Fetch collection
   * const data = await contentful.query(print(getAllPages), {
   *   locale: contentful.locale, // Automatically from site
   *   limit: 10,
   *   skip: 0,
   *   order: ['sys_publishedAt_DESC'],
   *   where: {},
   *   preview: false
   * });
   * const pages = data.pageCollection;
   *
   * @param {string} query - GraphQL query string (use `print()` to convert from AST)
   * @param {Object} [variables={}] - GraphQL query variables
   * @param {RequestInit} [options={}] - HTTP request options (headers, cache, etc.)
   * @returns {Promise<Object>} GraphQL response data
   */
  async query(query, variables = {}, options = {}) {
    // Preview mode: force `preview: true` so every existing hook (which passes
    // `preview: false`) requests drafts with zero hook edits, and tell the proxy
    // which site's Preview API credentials to use. The proxy resolves the
    // space/environment/token authoritatively from server config by siteId.
    if (this.preview) {
      // Send the in-memory preview secret to the proxy via a custom header. The
      // proxy validates it server-side and injects the Preview API token, so the
      // token never reaches the browser. Secret is captured from the `?secret=`
      // URL once, then stripped (see app/utils/preview-secret.js). README §5.2.
      return this.post(
        '',
        {
          query,
          variables: {...variables, preview: true},
          siteId: this.variables?.site?.id,
        },
        {
          ...options,
          headers: {
            ...(options.headers || {}),
            [PREVIEW_HEADER_NAME]: getPreviewSecret(),
          },
        }
      );
    }

    return this.post('', {query, variables}, options);
  }

  /**
   * @override
   *  - Handle GraphQL-specific errors
   *  - When both errors and data exist (e.g. one linked entry is draft), return partial data
   *    so other content can still render instead of failing the whole request
   *
   * @protected
   * @param {Response} response
   * @returns {Promise<object | string>}
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type');
    const body = contentType?.includes('json') ? await response.json() : await response.text();

    if (typeof body !== 'object' || body === null) {
      if (response.ok) {
        return body;
      }

      const error = new Error(`Contentful request failed (${response.status})`);

      error.status = response.status;

      throw error;
    }

    const {data, errors} = body;

    if (errors?.length) {
      const errorDetails = errors.map((e) => e.message).join('; ');
      const error = new Error(`Contentful GraphQL Error: ${errorDetails}`);

      error.name = 'ContentfulError';
      error.graphqlErrors = errors;
      error.status = response.status;

      // Return partial data when present (e.g. links with one draft entry omitted)
      // so callers can still render published embeds
      if (data != null) {
        return data;
      }

      throw error;
    }

    if (!response.ok) {
      const error = new Error(`Contentful request failed (${response.status})`);

      error.status = response.status;

      throw error;
    }

    return data;
  }
}
